// mission-lock.ts
//
// Machine lock `~/.avatar/autopilot.lock` (§9.4): CHỈ MỘT mission chạy trên một máy.
//
// Khác lock settings.json (#164) ở điểm CỐT TỬ: lock này **không bao giờ steal theo
// tuổi file**. mtime chỉ nói "lâu rồi không ai chạm", không phân biệt được
//   (a) supervisor chết thật, với (b) supervisor còn sống nhưng bận/treo, hoặc PID đã
// bị tái sử dụng. Đoán sai (a) khi thực tế là (b) ⇒ hai supervisor cùng ghi một
// mission. §9.4 buộc: PID reuse / liveness không rõ ⇒ RECOVERING, người quyết.
//
// Identity: missionId + ownerNonce + pid + processStartedAt (chống PID reuse) +
// childPids (con còn sống ⇒ KHÔNG spawn mission mới, §15) + heartbeat.

import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { dirname } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { classifyExistingLock } from "./mission-lock-classify-existing.js";
import { MachineLockBusyError } from "./mission-lock-errors.js";
import { type MachineLockFile, readMachineLock } from "./mission-lock-file-io.js";
import { type LockLivenessProbe, realLivenessProbe } from "./process-liveness-probe.js";

export {
  EXISTING_LOCK_READ_RETRIES,
  EXISTING_LOCK_READ_RETRY_DELAY_MS,
} from "./mission-lock-classify-existing.js";
export { MachineLockBusyError, MachineLockRecoveringError } from "./mission-lock-errors.js";
export { type MachineLockFile, readMachineLock } from "./mission-lock-file-io.js";
export { type LockLivenessProbe, realLivenessProbe } from "./process-liveness-probe.js";

export interface AcquireMachineLockOptions {
  missionId: string;
  liveness?: LockLivenessProbe;
  /** Ngưỡng heartbeat coi là "im lặng" — TỪ CONFIG (§18), không hardcode ở call site. */
  leaseStaleSec?: number;
  now?: () => Date;
}

export interface HeldMachineLock {
  readonly lockPath: string;
  readonly missionId: string;
  readonly ownerNonce: string;
  release: () => Promise<void>;
}

/**
 * Giành machine lock. Nguyên tử bằng `open(wx)` — hai supervisor start cùng lúc thì
 * đúng một thắng, kẻ thua nhận `MachineLockBusyError`.
 *
 * Gặp lock CÓ SẴN thì KHÔNG bao giờ tự xoá: phân loại rồi ném lỗi phù hợp.
 */
export async function acquireMachineLock(
  lockPath: string,
  options: AcquireMachineLockOptions,
): Promise<HeldMachineLock> {
  const now = options.now ?? (() => new Date());
  const liveness = options.liveness ?? realLivenessProbe;
  // R-25 (phase 09, N3): `leaseStaleSec` giữ trong AcquireMachineLockOptions
  // (API công khai, không đổi chữ ký) nhưng không còn đọc ở đây — hằng
  // "im lặng Xs" nó nuôi đã bị xoá khỏi thông báo lock mồ côi bên dưới.
  await ensureDir(dirname(lockPath));

  const ownerNonce = randomUUID();
  const startedAt = await liveness.processStartedAt(process.pid);
  const childPids = await liveness.descendantPids(process.pid);
  const payload: MachineLockFile = {
    missionId: options.missionId,
    ownerNonce,
    pid: process.pid,
    processStartedAt: startedAt,
    childPids,
    acquiredAt: now().toISOString(),
    heartbeatAt: now().toISOString(),
  };

  try {
    const handle = await fs.open(lockPath, "wx", 0o600);
    try {
      await handle.writeFile(`${JSON.stringify(payload)}\n`);
      await handle.sync();
    } finally {
      await handle.close();
    }
    return makeHeld(lockPath, payload);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== "EEXIST") throw err;
  }

  // Đã có lock — PHÂN LOẠI, tuyệt đối không xoá.
  await classifyExistingLock(lockPath, liveness);
  // classify luôn ném; dòng này chỉ để TS thấy hàm không rơi xuống.
  throw new MachineLockBusyError(`Machine lock đang được giữ: ${lockPath}`);
}

function makeHeld(lockPath: string, payload: MachineLockFile): HeldMachineLock {
  const current = payload;
  let released = false;
  return {
    lockPath,
    missionId: payload.missionId,
    ownerNonce: payload.ownerNonce,
    release: async () => {
      if (released) return;
      released = true;
      // Chỉ xoá nếu nonce còn đúng của mình — tránh xoá nhầm lock người khác vừa tạo.
      const onDisk = await readMachineLock(lockPath);
      if (onDisk?.ownerNonce === current.ownerNonce) {
        await fs.unlink(lockPath).catch(() => {});
      }
    },
  };
}
